import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image as PdfImage } from '@react-pdf/renderer'
import HeaderFashionOfPDF from './HeaderFashion'
import FooterOfPDF from '../Footer'
import { ProductData } from './technicalSheetPdfTypes'
import { SelectedSections } from '../../Modals/FashionProduct/GeneratePDFModal'
import { stripHtml } from '../../../utils/stripHtml'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 85,
    paddingBottom: 65,
    paddingHorizontal: 30,
  },
  pageTitle: {
    position: 'absolute',
    top: 56,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000',
  },
  pageNumber: {
    position: 'absolute',
    top: 26,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 8.5,
    color: '#000',
  },
  section: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 9.5,
    fontWeight: 'bold',
    backgroundColor: '#f3f4f6',
    padding: 2.5,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  col: {
    flex: 1,
    paddingRight: 6,
    minWidth: '24%',
    maxWidth: '25%',
  },
  colFull: {
    width: '100%',
    marginBottom: 4,
  },
  label: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#4b5563',
    marginBottom: 1,
  },
  value: {
    fontSize: 9,
    color: '#111827',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    marginVertical: 6,
  },
  compositionItem: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 6,
    borderRadius: 4,
    marginBottom: 8,
  },
  noteImagesRow: {
    flexDirection: 'column',
    marginTop: 4,
  },
  noteImageContainer: {
    width: '100%',
    height: 235,
    maxHeight: 240,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 3,
    marginBottom: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 3,
    backgroundColor: '#ffffff',
  },
  noteImage: {
    width: '100%',
    height: 205,
    maxHeight: 210,
    objectFit: 'contain',
    alignSelf: 'center',
  },
  approvalImageBadge: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    fontSize: 8.5,
    fontWeight: 'bold',
    paddingVertical: 2.5,
    paddingHorizontal: 10,
    borderRadius: 3,
    marginTop: 5,
    textAlign: 'center',
    alignSelf: 'center',
  },
})

interface TechnicalSheetPDFProps {
  data: ProductData
  sections: SelectedSections
  gradesByType: any
  gradeTypes: any[]
  coverImageUrl?: string | null
  attachmentImages?: string[]
  extraData?: any
  compositionItems?: any[]
  liveObservations?: string
  liveAnotacoes?: any[]
  liveAprovacoes?: any[]
  liveCores?: any[]
  liveMedidas?: number[]
}

const TechnicalSheetPDF: React.FC<TechnicalSheetPDFProps> = ({
  data,
  sections,
  gradesByType,
  gradeTypes,
  coverImageUrl,
  attachmentImages,
  extraData,
  compositionItems,
  liveObservations,
  liveAnotacoes,
  liveAprovacoes,
  liveCores,
  liveMedidas
}) => {

  const formatText = (text?: string | null) => {
    if (!text) return '-'
    return stripHtml(text)
  }

  const resolveSizeName = (val: any) => {
    if (typeof val !== 'number' && typeof val !== 'string') return val
    if (!gradesByType) return val
    const allGrades = Object.values(gradesByType).flat()
    const grade = (allGrades as any[]).find(g => g.id === Number(val))
    return grade ? grade.name : val
  }

  const formatSizeRange = (sizeRange: any) => {
    if (!sizeRange) return '-'
    if (Array.isArray(sizeRange)) {
      return sizeRange.map(v => resolveSizeName(v)).join(', ')
    }
    return String(sizeRange).split(',').map(v => resolveSizeName(v.trim())).join(', ')
  }

  const getItemLabel = (item: any) => {
    const rawTipo = String(item?.tipo || item?.type || '').toLowerCase()
    if (rawTipo === 'materia-prima' || rawTipo === 'materia_prima' || rawTipo === 'material') {
      return 'Material'
    }
    if (rawTipo === 'insumo' || rawTipo === 'trim' || rawTipo === 'resource') {
      return 'Insumo'
    }
    if (rawTipo === 'servico' || rawTipo === 'serviço' || rawTipo === 'service') {
      return 'Serviço'
    }
    if (item?.resourceId || item?.Resource || item?.resource) {
      return 'Insumo'
    }
    if (item?.serviceId || item?.Service || item?.service) {
      return 'Serviço'
    }
    return 'Material'
  }

  const getItemTypeValue = (item: any) => {
    const rawTipo = String(item?.tipo || item?.type || '').toLowerCase()
    if (rawTipo === 'materia-prima' || rawTipo === 'materia_prima' || rawTipo === 'material') {
      return 'Matéria-prima'
    }
    if (rawTipo === 'insumo' || rawTipo === 'trim' || rawTipo === 'resource') {
      return 'Insumo'
    }
    if (rawTipo === 'servico' || rawTipo === 'serviço' || rawTipo === 'service') {
      return 'Serviço'
    }
    if (item?.resourceId || item?.Resource || item?.resource) {
      return 'Insumo'
    }
    if (item?.serviceId || item?.Service || item?.service) {
      return 'Serviço'
    }
    return item?.tipo || item?.type || 'Matéria-prima'
  }

  const formatConsumoWithUnit = (consumoVal: any, unitVal: any) => {
    const consumo = consumoVal !== undefined && consumoVal !== null && String(consumoVal).trim() !== '' ? String(consumoVal).trim() : ''
    const unit = unitVal !== undefined && unitVal !== null && String(unitVal).trim() !== '' && String(unitVal).trim() !== '-' ? String(unitVal).trim() : ''
    
    if (!consumo) return '-'
    if (!unit) return consumo
    return `${consumo} ${unit}`
  }

  const formatNoteDate = (dateValue?: string | Date): string => {
    if (!dateValue) return ''
    const d = new Date(dateValue)
    if (isNaN(d.getTime())) return ''
    const dateStr = d.toLocaleDateString('pt-BR')
    const timeStr = d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })
    return `${dateStr} às ${timeStr}`
  }

  const firstImage = (attachmentImages && attachmentImages.length > 0)
    ? attachmentImages[0]
    : coverImageUrl

  const otherImages = (attachmentImages && attachmentImages.length > 1)
    ? attachmentImages.slice(1)
    : []

  const renderGeneralInfo = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>INFORMAÇÕES GERAIS</Text>
      <View style={styles.row}>
        <View style={styles.col}>
          <Text style={styles.label}>Código do produto</Text>
          <Text style={styles.value}>{formatText(data.productCode)}</Text>
        </View>
        <View style={styles.col}>
          <Text style={styles.label}>Nome do produto</Text>
          <Text style={styles.value}>{formatText(data.name)}</Text>
        </View>
        <View style={styles.col}>
          <Text style={styles.label}>Produto referência</Text>
          <Text style={styles.value}>{formatText(data.referenceProduct)}</Text>
        </View>
        <View style={styles.col}>
          <Text style={styles.label}>Descrição</Text>
          <Text style={styles.value}>{formatText(data.description)}</Text>
        </View>
      </View>

      {extraData && (
        <>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Valor (preço)</Text>
              <Text style={styles.value}>
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(extraData.price || 0)}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Coleção vinculada</Text>
              <Text style={styles.value}>{extraData.collectionName}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Família</Text>
              <Text style={styles.value}>{extraData.familyName}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Grupo</Text>
              <Text style={styles.value}>{extraData.groupName}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Episódio</Text>
              <Text style={styles.value}>{extraData.episodeName}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Tipo de graduação</Text>
              <Text style={styles.value}>{extraData.gradeTypeName}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Código fornecedor</Text>
              <Text style={styles.value}>{extraData.supplierCode}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Identif. do cliente</Text>
              <Text style={styles.value}>{extraData.clientIdentification}</Text>
            </View>
          </View>
        </>
      )}
    </View>
  )

  const renderCoverImageSection = () => (
    <View style={{ ...styles.section, flex: 1, marginBottom: 0 }}>
      <Text style={styles.sectionTitle}>IMAGEM DO PRODUTO</Text>
      {firstImage ? (
        <View style={{ width: '100%', flex: 1, justifyContent: 'center', alignItems: 'center', marginTop: 4 }}>
          <PdfImage
            src={firstImage.startsWith('data:') ? firstImage : { uri: firstImage, method: 'GET', headers: { 'Cache-Control': 'no-cache' } }}
            style={{ width: '100%', maxHeight: 520, objectFit: 'contain', alignSelf: 'center' }}  
          />
        </View>
      ) : (
        <View style={{ width: '100%', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 8, marginBottom: 8 }}>
          <Text style={styles.value}>Nenhuma imagem cadastrada.</Text>
        </View>
      )}
    </View>
  )

  const renderRemainingSections = () => (
    <>
      {/* SECTION: CORES */}
      {sections.cores && (
        <View style={styles.section}>
          <View minPresenceAhead={80} wrap={false}>
            <Text style={styles.sectionTitle}>CORES VINCULADAS</Text>
          </View>
          {liveCores && liveCores.length > 0 ? (
            <View style={styles.row}>
              {liveCores.map((color, idx) => (
                <View key={idx} style={{ ...styles.col, minWidth: '25%', marginBottom: 8 }}>
                  <Text style={styles.value}>• {color.name || color.itemColorName || 'Cor não definida'}</Text>
                </View>
              ))}
            </View>
          ) : data.productItemColors && data.productItemColors.length > 0 ? (
            <View style={styles.row}>
              {data.productItemColors.map((color, idx) => (
                <View key={idx} style={{ ...styles.col, minWidth: '25%', marginBottom: 8 }}>
                  <Text style={styles.value}>• {color.itemColorName || 'Cor não definida'}</Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.value}>Nenhuma cor vinculada.</Text>
          )}
        </View>
      )}

      {/* SECTION: COMPOSIÇÃO */}
      {sections.composicao && (
        <View style={styles.section}>
          <View minPresenceAhead={80} wrap={false}>
            <Text style={styles.sectionTitle}>COMPOSIÇÃO / INSUMOS</Text>
          </View>
          {compositionItems && compositionItems.length > 0 ? (
            compositionItems.map((item, idx) => (
              <View key={idx} style={styles.compositionItem} wrap={false} minPresenceAhead={40}>
                <View style={styles.row}>
                  <View style={styles.col}>
                    <Text style={styles.label}>{getItemLabel(item)}</Text>
                    <Text style={styles.value}>{item.material || item.descricao || item.description || item.stock?.description || item.resource?.name || '-'}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Tipo</Text>
                    <Text style={styles.value}>
                      {getItemTypeValue(item)}
                    </Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Acabamento</Text>
                    <Text style={styles.value}>{item.finishing || item.acabamento || '-'}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Consumo</Text>
                    <Text style={styles.value}>{formatConsumoWithUnit(item.consumo || item.usage, item.unidadeMedida || item.unitOfMeasurement?.abbreviation || item.unit)}</Text>
                  </View>
                </View>
                <View style={{ ...styles.row, marginTop: 4 }}>
                  <View style={styles.col}>
                    <Text style={styles.label}>Faixa de Tamanho</Text>
                    <Text style={styles.value}>{formatSizeRange(item.sizeRange)}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Custo/Un.</Text>
                    <Text style={styles.value}>{item.costPerUnit || item.cost || '-'}</Text>
                  </View>
                  <View style={styles.col}></View>
                  <View style={styles.col}></View>
                </View>
              </View>
            ))
          ) : data.composition?.variations && data.composition.variations.length > 0 ? (
            data.composition.variations.map((item: any, idx: number) => (
              <View key={idx} style={styles.compositionItem} wrap={false} minPresenceAhead={40}>
                <View style={styles.row}>
                  <View style={styles.col}>
                    <Text style={styles.label}>{getItemLabel(item)}</Text>
                    <Text style={styles.value}>{item.description || item.stock?.description || item.resource?.name || '-'}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Tipo</Text>
                    <Text style={styles.value}>{getItemTypeValue(item)}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Acabamento</Text>
                    <Text style={styles.value}>{item.finishing || '-'}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Consumo</Text>
                    <Text style={styles.value}>{formatConsumoWithUnit(item.usage, item.unitOfMeasurement?.abbreviation || item.unit)}</Text>
                  </View>
                </View>
                <View style={{ ...styles.row, marginTop: 4 }}>
                  <View style={styles.col}>
                    <Text style={styles.label}>Faixa de Tamanho</Text>
                    <Text style={styles.value}>{formatSizeRange(item.sizeRange)}</Text>
                  </View>
                  <View style={styles.col}>
                    <Text style={styles.label}>Custo/Un.</Text>
                    <Text style={styles.value}>{item.cost || '-'}</Text>
                  </View>
                  <View style={styles.col}></View>
                  <View style={styles.col}></View>
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.value}>Nenhum insumo ou composição cadastrada.</Text>
          )}
        </View>
      )}

      {/* SECTION: MEDIDAS */}
      {sections.medidas && (
        <View style={styles.section}>
          <View minPresenceAhead={80} wrap={false}>
            <Text style={styles.sectionTitle}>GRADE / MEDIDAS</Text>
          </View>
          {data.gradeInProduct && data.gradeInProduct.length > 0 ? (
            <View style={styles.row}>
              <Text style={styles.value}>
                Tamanhos: {data.gradeInProduct.map(g => g.grade?.name).join(', ')}
              </Text>
            </View>
          ) : (
            <Text style={styles.value}>Nenhuma medida ou grade cadastrada.</Text>
          )}
        </View>
      )}

      {/* SECTION: ANOTAÇÕES */}
      {sections.anotacoes && (
        <View style={styles.section}>
          <View minPresenceAhead={80} wrap={false}>
            <Text style={styles.sectionTitle}>ANOTAÇÕES E OBSERVAÇÕES</Text>
          </View>
          
          {(liveObservations || data.observations) && (
            <View style={{ marginBottom: 8 }} wrap={false}>
              <Text style={{ ...styles.label, marginBottom: 2 }}>Geral</Text>
              <Text style={styles.value}>{formatText(liveObservations || data.observations)}</Text>
            </View>
          )}

          {liveAnotacoes && liveAnotacoes.length > 0 && liveAnotacoes.map((nota, idx) => {
            const author = nota.createdByName || nota.CreatedBy?.name || nota.authorName || nota.userName || null
            const updatedAuthor = nota.updatedByName || nota.UpdatedBy?.name || null
            const createdDateStr = formatNoteDate(nota.createdAt)
            const updatedDateStr = formatNoteDate(nota.updatedAt)
            const sectorName = nota.sectorName || nota.Sector?.name || null
            const noteBaseTitle = nota.title || `Nota ${idx + 1}`
            const noteTitle = sectorName ? `${noteBaseTitle} - ${sectorName}` : noteBaseTitle
            const noteImages = [
              ...(nota.imageUrls || []),
              ...(nota.imagePreviews || []),
              ...(nota.Files?.map((f: any) => f.GeneralFile?.fileUrl) || []),
            ].filter((u: any): u is string => typeof u === 'string' && u.length > 0)
            const uniqueImages = Array.from(new Set(noteImages))

            return (
              <View key={idx} style={{ marginBottom: 12, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' }}>
                <View wrap={false} minPresenceAhead={40}>
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Text style={{ ...styles.label, marginBottom: 0 }}>{noteTitle}</Text>
                    <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      {author && (
                        <Text style={{ ...styles.label, fontSize: 8, color: '#6b7280', marginBottom: 0 }}>
                          Criado por: <Text style={{ color: '#374151', fontWeight: 'bold' }}>{author}</Text>{createdDateStr ? ` em ${createdDateStr}` : ''}
                        </Text>
                      )}
                      {updatedAuthor && (
                        <Text style={{ ...styles.label, fontSize: 8, color: '#6b7280', marginBottom: 0 }}>
                          {author ? ' • ' : ''}Editado por: <Text style={{ color: '#374151', fontWeight: 'bold' }}>{updatedAuthor}</Text>{updatedDateStr ? ` em ${updatedDateStr}` : ''}
                        </Text>
                      )}
                    </View>
                  </View>
                  <Text style={styles.value}>{formatText(nota.text || nota.description || nota.observacao)}</Text>
                </View>
                {uniqueImages.length > 0 && (
                  <View style={styles.noteImagesRow}>
                    {uniqueImages.map((imgUrl, imgIdx) => (
                      <View key={imgIdx} style={styles.noteImageContainer} wrap={false}>
                        <PdfImage
                          src={imgUrl.startsWith('data:') ? imgUrl : { uri: imgUrl, method: 'GET', headers: { 'Cache-Control': 'no-cache' } }}
                          style={styles.noteImage}
                        />
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )
          })}

          {(!liveObservations && !data.observations && (!liveAnotacoes || liveAnotacoes.length === 0)) && (
            <Text style={styles.value}>Nenhuma anotação cadastrada.</Text>
          )}
        </View>
      )}

      {/* SECTION: APROVAÇÕES */}
      {sections.aprovacoes && (
        <View style={styles.section}>
          <View minPresenceAhead={100} wrap={false}>
            <Text style={styles.sectionTitle}>APROVAÇÕES</Text>
          </View>
          {liveAprovacoes && liveAprovacoes.length > 0 ? (
            liveAprovacoes.map((aprov, idx) => {
              const formatDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '-';
              const frontUrl = aprov.FrontImage?.fileUrl || aprov.frontImage?.fileUrl || (typeof aprov.FrontImage === 'string' ? aprov.FrontImage : null) || aprov.frontImageUrl
              const sideUrl = aprov.SideImage?.fileUrl || aprov.sideImage?.fileUrl || (typeof aprov.SideImage === 'string' ? aprov.SideImage : null) || aprov.sideImageUrl
              const backUrl = aprov.BackImage?.fileUrl || aprov.backImage?.fileUrl || (typeof aprov.BackImage === 'string' ? aprov.BackImage : null) || aprov.backImageUrl

              const approvalImages = [
                frontUrl ? { label: 'Frente', url: frontUrl } : null,
                sideUrl ? { label: 'Lateral', url: sideUrl } : null,
                backUrl ? { label: 'Costas', url: backUrl } : null,
              ].filter((item): item is { label: string; url: string } => Boolean(item && item.url))

              return (
                <View key={idx} style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>
                  <View wrap={false} minPresenceAhead={60}>
                    <View style={{ ...styles.row, marginBottom: 4 }}>
                      <View style={styles.col}>
                        <Text style={styles.label}>Nome</Text>
                        <Text style={styles.value}>{aprov.name || '-'}</Text>
                      </View>
                      <View style={styles.col}>
                        <Text style={styles.label}>Status</Text>
                        <Text style={styles.value}>
                          {aprov.status === 'IN_PROGRESS' ? 'Em andamento' : 
                           aprov.status === 'APPROVED' ? 'Aprovado' : 
                           aprov.status === 'REJECTED' ? 'Reprovado' : aprov.status || '-'}
                        </Text>
                      </View>
                      <View style={styles.col}>
                        <Text style={styles.label}>Fornecedor</Text>
                        <Text style={styles.value}>{aprov.SupplierCompany?.companyName || '-'}</Text>
                      </View>
                      <View style={styles.col}>
                        <Text style={styles.label}>Criado por</Text>
                        <Text style={styles.value}>{aprov.CreatedBy?.name || '-'}</Text>
                      </View>
                    </View>
                    <View style={styles.row}>
                      <View style={styles.col}>
                        <Text style={styles.label}>Aprovador</Text>
                        <Text style={styles.value}>{aprov.Approver?.name || '-'}</Text>
                      </View>
                      <View style={styles.col}>
                        <Text style={styles.label}>Data Início</Text>
                        <Text style={styles.value}>{formatDate(aprov.startDate)}</Text>
                      </View>
                      <View style={styles.col}>
                        <Text style={styles.label}>Entrega Prevista</Text>
                        <Text style={styles.value}>{formatDate(aprov.expectedDeliveryDate)}</Text>
                      </View>
                      <View style={styles.col}>
                        <Text style={styles.label}>Valid./Tam.</Text>
                        <Text style={styles.value}>{(aprov.sizes ?? []).map((id: any) => resolveSizeName(id)).join(', ') || '-'}</Text>
                      </View>
                    </View>
                  </View>

                  {approvalImages.length > 0 && (
                    <View style={styles.noteImagesRow}>
                      {approvalImages.map((imgItem, imgIdx) => (
                        <View key={imgIdx} style={styles.noteImageContainer} wrap={false}>
                          <PdfImage
                            src={imgItem.url.startsWith('data:') ? imgItem.url : { uri: imgItem.url, method: 'GET', headers: { 'Cache-Control': 'no-cache' } }}
                            style={styles.noteImage}
                          />
                          <Text style={styles.approvalImageBadge}>{imgItem.label.toUpperCase()}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )
            })
          ) : (
            <Text style={styles.value}>Nenhuma aprovação cadastrada.</Text>
          )}
        </View>
      )}
    </>
  )

  const hasOtherSections = sections.cores || sections.composicao || sections.medidas || sections.anotacoes || sections.aprovacoes

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderFashionOfPDF />
        
        <Text style={styles.pageTitle} fixed>
          FICHA TÉCNICA - {data.productCode}
        </Text>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${pageNumber} / ${totalPages}`
        )} fixed />

        {renderGeneralInfo()}
        {sections.imagens ? (
          renderCoverImageSection()
        ) : (
          renderRemainingSections()
        )}

        <FooterOfPDF />
      </Page>

      {sections.imagens && (otherImages.length > 0 || hasOtherSections) && (
        <Page size="A4" style={styles.page}>
          <HeaderFashionOfPDF />
          
          <Text style={styles.pageTitle} fixed>
            FICHA TÉCNICA - {data.productCode}
          </Text>

          <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
            `${pageNumber} / ${totalPages}`
          )} fixed />

          {otherImages.length > 0 && (
            <View style={styles.section}>
              <View minPresenceAhead={100} wrap={false}>
                <Text style={styles.sectionTitle}>
                  {otherImages.length > 1 ? 'IMAGENS DO PRODUTO' : 'IMAGEM DO PRODUTO'}
                </Text>
              </View>
              <View style={styles.noteImagesRow}>
                {otherImages.map((imageSrc, idx) => (
                  <View key={idx} style={styles.noteImageContainer} wrap={false}>
                    <PdfImage
                      src={imageSrc.startsWith('data:') ? imageSrc : { uri: imageSrc, method: 'GET', headers: { 'Cache-Control': 'no-cache' } }}
                      style={styles.noteImage}
                    />
                  </View>
                ))}
              </View>
            </View>
          )}

          {renderRemainingSections()}

          <FooterOfPDF />
        </Page>
      )}
    </Document>
  )
}

export default TechnicalSheetPDF
